import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Field, Select, Modal } from "@/components/common";

const HELP = {
  google: "Gmail / Google Workspace: turn on 2-Step Verification, then create an App password (Google Account → Security → App passwords). Use that 16-character password here, not your normal password.",
  microsoft: "Microsoft 365 / Outlook: your admin must allow SMTP AUTH and IMAP for this mailbox. With MFA on, create an app password. If your tenant blocks this, use “Sign in with Microsoft” above instead.",
  other: "Enter your mail provider's SMTP and IMAP server details.",
};

const OAUTH = {
  microsoft: { api: "microsoft", name: "Microsoft", tid: "ms", text: "Works even when app passwords, SMTP AUTH or IMAP are blocked. RingUs asks only to send mail and read inbox headers for reply detection. Your organisation's admin may need to approve it once." },
  google: { api: "google", name: "Google", tid: "google", text: "No app password needed. RingUs asks only to send email and read message headers (sender, subject) for reply detection — never message contents." },
};

function OAuthSignIn({ sender, provider }) {
  const cfg = OAUTH[provider];
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/${cfg.api}/status`).then((r) => setSt(r.data)); }, [cfg.api]);
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/senders/${sender.id}/${cfg.api}/start`); window.location.href = data.url; } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  if (!st) return null;
  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50 p-3" data-testid={`${cfg.tid}-signin-panel`}>
      <p className="text-sm font-semibold text-slate-800">Recommended: sign in with {cfg.name}</p>
      <p className="mb-2 text-xs text-slate-600">{cfg.text}</p>
      {st.configured ? (
        <button data-testid={`${cfg.tid}-signin-btn`} disabled={busy} onClick={go} className="cd-btn-primary w-full">{busy ? `Opening ${cfg.name}…` : `Sign in with ${cfg.name}`}</button>
      ) : (
        <p data-testid={`${cfg.tid}-setup-required`} className="rounded-lg bg-white p-2 text-xs text-amber-800">Setup needed: the app owner must add the {cfg.name} OAuth client ID and secret. Redirect URI to register: <span className="break-all font-mono">{st.redirect_uri}</span></p>
      )}
    </div>
  );
}

export default function MailboxDialog({ sender, onClose, onDone }) {
  const mb = sender.mailbox || {};
  const [presets, setPresets] = useState({});
  const [f, setF] = useState({ provider: mb.provider || "google", email: mb.email || "", username: mb.username || "", password: "", imap_enabled: mb.imap_enabled ?? true,
    smtp_host: mb.smtp_host || "", smtp_port: mb.smtp_port || "", smtp_security: mb.smtp_security || "", imap_host: mb.imap_host || "", imap_port: mb.imap_port || "", daily_limit: mb.daily_limit || "" });
  const [adv, setAdv] = useState(f.provider === "other");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => {
    api.get("/mailbox-presets").then((r) => {
      setPresets(r.data);
      if (!mb.email) {
        const pr = r.data[f.provider] || {};
        setF((x) => ({ ...x, smtp_host: pr.smtp_host, smtp_port: pr.smtp_port, smtp_security: pr.smtp_security, imap_host: pr.imap_host, imap_port: pr.imap_port, daily_limit: pr.daily_limit }));
      }
    });
  }, []); // eslint-disable-line
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const pickProvider = (p) => {
    const pr = presets[p] || {};
    setF((x) => ({ ...x, provider: p, smtp_host: pr.smtp_host, smtp_port: pr.smtp_port, smtp_security: pr.smtp_security, imap_host: pr.imap_host, imap_port: pr.imap_port, daily_limit: pr.daily_limit }));
    if (p === "other") setAdv(true);
  };
  const save = async () => {
    setBusy(true);
    setResult(null);
    try {
      const body = { ...f, smtp_port: Number(f.smtp_port) || null, imap_port: Number(f.imap_port) || null, daily_limit: Number(f.daily_limit) || null, password: f.password || null, username: f.username || null };
      const { data } = await api.put(`/senders/${sender.id}/mailbox`, body);
      setResult(data);
      if (data.ok) { toast.success("Mailbox connected and verified"); onDone(); }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Modal open onClose={onClose} title={`Connect mailbox — ${sender.business_name}`} wide testid="mailbox-dialog">
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1">
          {[["google", "Google / Gmail"], ["microsoft", "Microsoft 365"], ["other", "Other"]].map(([p, l]) => (
            <button key={p} data-testid={`mailbox-provider-${p}`} onClick={() => pickProvider(p)} className={`h-11 rounded-lg text-sm font-semibold ${f.provider === p ? "bg-white text-teal-700 shadow-sm" : "text-slate-600"}`}>{l}</button>
          ))}
        </div>
        {OAUTH[f.provider] && <OAuthSignIn sender={sender} provider={f.provider} />}
        <p className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600">{OAUTH[f.provider] ? "Or use an app password: " : ""}{HELP[f.provider]}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Mailbox email (emails are sent From this address)"><input data-testid="mailbox-email-input" className="cd-input" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="you@yourbusiness.com" /></Field>
          <Field label={mb.email ? "App password (re-enter if you change server or address)" : "App password"}><input data-testid="mailbox-password-input" type="password" autoComplete="new-password" className="cd-input" value={f.password} onChange={(e) => set("password", e.target.value)} /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="mailbox-imap-toggle" className="h-5 w-5 accent-teal-600" checked={f.imap_enabled} onChange={(e) => set("imap_enabled", e.target.checked)} />
          Detect replies in this inbox (reads only sender/subject headers, never marks emails as read)</label>
        <button onClick={() => setAdv(!adv)} data-testid="mailbox-advanced-toggle" className="text-sm font-semibold text-teal-700">{adv ? "Hide" : "Show"} server settings</button>
        {adv && (
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Username (if different)"><input className="cd-input" value={f.username} onChange={(e) => set("username", e.target.value)} /></Field>
            <Field label="SMTP host"><input data-testid="mailbox-smtp-host" className="cd-input" value={f.smtp_host} onChange={(e) => set("smtp_host", e.target.value)} /></Field>
            <Field label="SMTP port"><input className="cd-input" value={f.smtp_port} onChange={(e) => set("smtp_port", e.target.value)} /></Field>
            <Field label="Security"><Select value={f.smtp_security} onChange={(v) => set("smtp_security", v)} options={[["starttls", "STARTTLS (587)"], ["ssl", "SSL/TLS (465)"]]} /></Field>
            <Field label="IMAP host"><input data-testid="mailbox-imap-host" className="cd-input" value={f.imap_host} onChange={(e) => set("imap_host", e.target.value)} /></Field>
            <Field label="IMAP port"><input className="cd-input" value={f.imap_port} onChange={(e) => set("imap_port", e.target.value)} /></Field>
            <Field label="Daily send cap" hint="Keep under your provider's limit"><input className="cd-input" value={f.daily_limit} onChange={(e) => set("daily_limit", e.target.value)} /></Field>
          </div>
        )}
        {result && !result.ok && <p data-testid="mailbox-test-error" className="rounded-xl bg-rose-50 p-3 text-sm text-rose-700">Connection test failed — {result.error}</p>}
        <p className="text-xs text-slate-500">The password is encrypted and stored on the server only. It is never shown again or sent to your browser.</p>
        <button data-testid="mailbox-save-btn" disabled={busy || !f.email || (!f.password && !mb.email)} onClick={save} className="cd-btn-primary w-full">{busy ? "Testing connection…" : "Test & connect"}</button>
      </div>
    </Modal>
  );
}
